/**
 * PwaLogin - the ONE restaurant-account sign-in screen of every PWA.
 *
 * Same account as Back Office: email + password (POST /api/merchant/login),
 * then the SMS / authenticator code when 2FA is on (the mfa_token proves the
 * password step), or Google (POST /api/merchant/google-login). An owner of
 * several locations picks which one this device is for.
 *
 * By default the answer is saved in the shared PWA account store
 * (lib/pwaAccount). With `persist={false}` (kiosk set-up: the kiosk keeps only
 * its own device token) the answer is handed to `onSignedIn` instead.
 */

import React, { useState } from 'react';
import axios from 'axios';
import { Loader2, LogIn, Store } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import GoogleSignInButton from '@/components/GoogleSignInButton';
import { saveAccount, takeSignedOutReason, apiErrorText } from '@/lib/pwaAccount';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function PwaLogin({
  appName,
  subtitle = 'Sign in with your restaurant account (the same login as Back Office).',
  icon: Icon = Store,
  accent = '#f97316',
  persist = true,
  onSignedIn,
  children, // extra fields shown above email (e.g. kiosk name)
  canSubmit = true,
  footer = null, // shown under the sign-in card (e.g. kiosk: use a pairing code)
}) {
  const [step, setStep] = useState('credentials'); // credentials | code | location
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(() => takeSignedOutReason());
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfa, setMfa] = useState(null); // { token, method: 'sms' | 'totp', phone }
  const [code, setCode] = useState('');
  const [pending, setPending] = useState(null); // a login answer waiting for a location pick

  const finish = async (data, restaurantId) => {
    if (persist) {
      const account = saveAccount(data, restaurantId);
      if (onSignedIn) await onSignedIn(account, data);
    } else if (onSignedIn) {
      await onSignedIn(null, data, restaurantId);
    }
  };

  const signedIn = async (data) => {
    const restaurants = Array.isArray(data.restaurants) ? data.restaurants : [];
    if (restaurants.length > 1) {
      setPending(data);
      setStep('location');
      return;
    }
    await finish(data);
  };

  const run = async (fn, fallback) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err?.response ? apiErrorText(err, fallback) : (err?.message || fallback));
    } finally {
      setBusy(false);
    }
  };

  const sendSms = async (mfaToken) => {
    await axios.post(`${API}/api/otp/send-sms`, { mfa_token: mfaToken });
  };

  const handleLogin = (e) => {
    e.preventDefault();
    run(async () => {
      const res = await axios.post(`${API}/api/merchant/login`, { email: email.trim(), password });
      if (res.data.requires_otp) {
        const method = res.data.totp_enabled ? 'totp' : 'sms';
        if (method === 'sms') await sendSms(res.data.mfa_token);
        setMfa({ token: res.data.mfa_token, method, phone: res.data.phone_masked });
        setCode('');
        setStep('code');
      } else {
        await signedIn(res.data);
      }
    }, 'Sign in failed');
  };

  const handleCode = (e) => {
    e.preventDefault();
    run(async () => {
      const res = mfa.method === 'sms'
        ? await axios.post(`${API}/api/otp/verify-sms`, { mfa_token: mfa.token, otp_code: code })
        : await axios.post(`${API}/api/otp/verify-totp`, { mfa_token: mfa.token, totp_code: code });
      await signedIn(res.data);
    }, 'That code did not work');
  };

  const handleGoogle = (credential) => run(async () => {
    const res = await axios.post(`${API}/api/merchant/google-login`, { credential });
    await signedIn(res.data);
  }, 'Google sign-in failed');

  const pickLocation = (restaurantId) => run(() => finish(pending, restaurantId), 'Could not use this location');

  const card = 'rounded-2xl p-6 border border-white/10 bg-slate-800/80 space-y-4';
  const inputCls = 'bg-white/5 border-white/10 text-white h-12';

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-900" data-testid="pwa-login">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="mx-auto mb-3 w-16 h-16 rounded-full flex items-center justify-center" style={{ background: `${accent}22` }}>
            <Icon className="w-8 h-8" style={{ color: accent }} />
          </div>
          <h1 className="text-2xl font-bold text-white">{appName}</h1>
          <p className="text-slate-400 text-sm mt-1">{subtitle}</p>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200" role="alert" data-testid="pwa-login-error">
            {error}
          </div>
        )}

        {step === 'credentials' && (
          <form onSubmit={handleLogin} className={card}>
            {children}
            <Input className={inputCls} type="email" placeholder="Email" autoComplete="username" value={email}
              onChange={e => setEmail(e.target.value)} data-testid="pwa-login-email" />
            <PasswordInput className={inputCls} placeholder="Password" autoComplete="current-password" value={password}
              onChange={e => setPassword(e.target.value)} data-testid="pwa-login-password" />
            <Button type="submit" disabled={busy || !email || !password || !canSubmit}
              className="w-full h-12 text-white" style={{ background: accent }} data-testid="pwa-login-submit">
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><LogIn className="w-4 h-4 mr-2" /> Sign in</>}
            </Button>
            <div className="flex items-center gap-3 text-xs text-slate-500">
              <div className="h-px flex-1 bg-white/10" /> or <div className="h-px flex-1 bg-white/10" />
            </div>
            <GoogleSignInButton
              onSuccess={(credential) => (canSubmit ? handleGoogle(credential) : setError('Fill in the field above first'))}
              onError={() => setError('Google sign-in failed')}
            />
          </form>
        )}

        {step === 'code' && mfa && (
          <form onSubmit={handleCode} className={card}>
            <p className="text-slate-300 text-sm">
              {mfa.method === 'sms'
                ? `Enter the 6-digit code we sent to ${mfa.phone || 'your phone'}.`
                : 'Enter the 6-digit code from your authenticator app.'}
            </p>
            <Input className={`${inputCls} text-center tracking-[0.5em] text-xl`} inputMode="numeric" maxLength={6}
              autoFocus value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))} data-testid="pwa-login-code" />
            <Button type="submit" disabled={busy || code.length !== 6} className="w-full h-12 text-white" style={{ background: accent }}>
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verify'}
            </Button>
            {mfa.method === 'sms' && (
              <button type="button" className="w-full text-sm text-slate-300" disabled={busy}
                onClick={() => run(async () => { await sendSms(mfa.token); setError(null); }, 'Could not send a new code')}>
                Send a new code
              </button>
            )}
            <button type="button" className="w-full text-sm text-slate-500" onClick={() => { setStep('credentials'); setMfa(null); }}>
              Back
            </button>
          </form>
        )}

        {step === 'location' && pending && (
          <div className={card}>
            <p className="text-slate-300 text-sm">Which location is this device for?</p>
            {pending.restaurants.map(r => (
              <Button key={r.id} variant="outline" disabled={busy} onClick={() => pickLocation(r.id)}
                className="w-full h-12 justify-start border-white/10 bg-white/5 text-white" data-testid={`pwa-login-location-${r.id}`}>
                {r.name}{String(r.id) === String(pending.primary_restaurant_id) ? ' (primary)' : ''}
              </Button>
            ))}
          </div>
        )}

        {footer && <div className="mt-4">{footer}</div>}
      </div>
    </div>
  );
}
